/**
 * SystemModeContext
 * ───────────────────────────────────────────────────────────────
 * ERP ↔ HR 시스템 모드 전환을 관장하는 전역 컨텍스트.
 * 디자인 파일(hr-system/project/HR System.html)의 App 컴포넌트
 * 로직을 React Context 패턴으로 이식했다.
 *
 * 주요 책임
 * - 현재 모드(`'erp' | 'hr'`) 상태 관리
 * - localStorage 영속성 (`hr_system` 키)
 * - 전환 애니메이션 타이밍 (180ms 후 교체, 420ms 후 페이드 복귀)
 * - URL prefix(`/hr/*`)에 따른 초기 모드 결정
 * - 모드별 최근 탭 저장 (`hr_tab`, `erp_tab`)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

export type SystemMode = 'erp' | 'hr';

interface SystemModeContextValue {
  mode: SystemMode;
  setMode: (next: SystemMode) => void;
  switching: boolean;          // 전환 애니메이션 진행 중 여부
  animateSwitch: boolean;      // 전환 애니메이션 사용 여부 (기본 true)
  setAnimateSwitch: (v: boolean) => void;
  /** 현재 모드의 accent 색상 토큰 */
  accent: {
    text: string;              // 강조 텍스트 색상
    bg: string;                // 활성 배경 tint
    dot: string;               // active 세그먼트 dot
  };
}

const STORAGE_KEY_MODE = 'hr_system';
const STORAGE_KEY_ANIMATE = 'hr_system_animate';

const ACCENTS: Record<SystemMode, SystemModeContextValue['accent']> = {
  erp: {
    text: '#097fe8',   // Notion Blue Text
    bg: '#f2f9ff',     // Badge Blue Background
    dot: '#097fe8',
  },
  hr: {
    text: '#128a2c',   // HR Green Text (deep)
    bg: '#eafaec',     // HR Green Background
    dot: '#1aae39',    // HR Green Dot (brighter)
  },
};

const SystemModeContext = createContext<SystemModeContextValue | null>(null);

/**
 * URL을 기반으로 초기 모드 결정.
 * 우선순위: URL(`/hr/...`) > localStorage > 기본 'erp'
 */
const resolveInitialMode = (): SystemMode => {
  if (typeof window === 'undefined') return 'erp';
  const path = window.location.pathname;
  if (path.startsWith('/hr')) return 'hr';
  const stored = window.localStorage.getItem(STORAGE_KEY_MODE);
  if (stored === 'hr' || stored === 'erp') return stored;
  return 'erp';
};

const resolveInitialAnimate = (): boolean => {
  if (typeof window === 'undefined') return true;
  const stored = window.localStorage.getItem(STORAGE_KEY_ANIMATE);
  if (stored === 'false') return false;
  return true;
};

interface ProviderProps {
  children: ReactNode;
}

export const SystemModeProvider: React.FC<ProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<SystemMode>(resolveInitialMode);
  const [switching, setSwitching] = useState(false);
  const [animateSwitch, setAnimateSwitchState] = useState<boolean>(resolveInitialAnimate);
  const location = useLocation();

  // URL 기반 모드 자동 동기화 (수동 URL 입력/뒤로가기 시)
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/hr') && mode !== 'hr') {
      setModeState('hr');
    }
    // ERP로 자동 되돌림은 하지 않음 (사용자가 명시적으로 토글해야 함)
  }, [location.pathname, mode]);

  // mode 변경 시 localStorage 반영 + body 에 data-hr-mode 속성 (Sprint 18 모바일 CSS 트리거)
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_MODE, mode);
      document.body.setAttribute('data-hr-mode', mode === 'hr' ? 'true' : 'false');
    } catch {
      /* storage quota/incognito: 무시 */
    }
  }, [mode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_ANIMATE, String(animateSwitch));
    } catch {
      /* 무시 */
    }
  }, [animateSwitch]);

  const setMode = useCallback(
    (next: SystemMode) => {
      if (next === mode) return;
      if (animateSwitch) {
        setSwitching(true);
        // 180ms: 페이드 아웃 완료 시점에 실제 모드 교체
        window.setTimeout(() => setModeState(next), 180);
        // 420ms: 페이드 인 완료 후 switching 플래그 해제
        window.setTimeout(() => setSwitching(false), 420);
      } else {
        setModeState(next);
      }
    },
    [mode, animateSwitch],
  );

  const setAnimateSwitch = useCallback((v: boolean) => {
    setAnimateSwitchState(v);
  }, []);

  const value = useMemo<SystemModeContextValue>(
    () => ({
      mode,
      setMode,
      switching,
      animateSwitch,
      setAnimateSwitch,
      accent: ACCENTS[mode],
    }),
    [mode, setMode, switching, animateSwitch, setAnimateSwitch],
  );

  return <SystemModeContext.Provider value={value}>{children}</SystemModeContext.Provider>;
};

export const useSystemMode = (): SystemModeContextValue => {
  const ctx = useContext(SystemModeContext);
  if (!ctx) {
    throw new Error('useSystemMode must be used within a <SystemModeProvider>');
  }
  return ctx;
};

/** 테스트 편의를 위해 export */
export const __SYSTEM_MODE_STORAGE_KEY__ = STORAGE_KEY_MODE;
